import React, { useState, useMemo } from "react";
// TODO: Implement custom API client
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { subDays, startOfDay, endOfDay } from "date-fns";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { ArrowLeft, Pencil, Zap, Users, Building2, TrendingUp, CheckCircle2, XCircle, Clock, Activity, Shield, Percent, Receipt } from "lucide-react";
import { mapGlobalTransactions } from "@/shared/utils/transactionMappers";
import TransactionDetailPanel from "@/shared/components/admin/TransactionDetailPanel";
import DetailStatCard from "@/shared/components/admin/DetailStatCard";
import DetailHeaderSkeleton from "@/shared/components/skeletons/DetailHeaderSkeleton";
import DetailStatCardSkeleton from "@/shared/components/skeletons/DetailStatCardSkeleton";
import AnalyticsTab from "@/shared/components/admin/engineDetail/AnalyticsTab";
import TransactionsTab from "@/shared/components/admin/engineDetail/TransactionsTab";
import ResellersTab from "@/shared/components/admin/engineDetail/ResellersTab";
import MerchantsTab from "@/shared/components/admin/engineDetail/MerchantsTab";
import * as adminService from "@/shared/api/services/adminService";
import * as transactionService from "@/shared/api/services/transactionService";
import * as merchantService from "@/shared/api/services/merchantService";
import * as engineService from "@/shared/api/services/engineService";
import { useLanguage } from "@/shared/components/shared/LanguageContext";


const statusConfig = {
  active: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: "Active" },
  suspended: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Suspended" },
  pending: { icon: Clock, color: "bg-amber-100 text-amber-700", label: "Pending" },
};

export default function AdminEngineDetail() {
  const { t } = useLanguage();
  const urlParams = new URLSearchParams(window.location.search);
  const engineId = urlParams.get("id");

  const [selectedTx, setSelectedTx] = useState(null);
  const [txFilters, setTxFilters] = useState({ search: "", status: "all", type: "all", reseller: "all", merchant: "all" });
  const [page, setPage] = useState(0);
  const pageSize = 20;

  // Fetch engine details
  const { data: engine } = useQuery({
    queryKey: ["engine-detail", engineId],
    queryFn: () => adminService.getEngineById(engineId),
    enabled: !!engineId,
  });

  // Fetch all resellers
  const { data: allResellers = [] } = useQuery({
    queryKey: ["resellers"],
    queryFn: adminService.getAllResellers,
  });

  // Filter resellers created by this engine
  const resellers = useMemo(
    () => allResellers.filter(r => r.createdByEngineId === engineId),
    [allResellers, engineId]
  );

  // Fetch engine-specific merchants (for the Merchants tab)
  const { data: merchants = [] } = useQuery({
    queryKey: ["engine-merchants", engineId],
    queryFn: () => adminService.getMerchantsByEngine(engineId),
    enabled: !!engineId,
  });

  // Fetch only the merchant IDs array directly from the backend (NEW METHOD)
  const { data: merchantIds = [] } = useQuery({
    queryKey: ["engine-merchant-ids", engineId],
    queryFn: () => engineService.getMerchantsByEngineId(engineId),
    enabled: !!engineId,
  });

  const { data: allTransactionsResult, isLoading: txLoading } = useQuery({
    queryKey: ["engine-transactions", merchantIds, page, pageSize, txFilters],
    queryFn: () => {
      const backendFilters = {};
      if (txFilters.status !== "all") backendFilters.status = txFilters.status;
      if (txFilters.type !== "all") backendFilters.type = txFilters.type;

      return transactionService.getPaginatedTransactions(
        page + 1,
        pageSize,
        txFilters.search,
        backendFilters,
        merchantIds
      );
    },
    enabled: merchantIds.length > 0,
    keepPreviousData: true,
  });

  const transactions = useMemo(() => mapGlobalTransactions(allTransactionsResult?.data), [allTransactionsResult]);
  const totalPages = allTransactionsResult?.totalPages || 1;

  const chartStartISO = startOfDay(subDays(new Date(), 6)).toISOString();
  const chartEndISO = endOfDay(new Date()).toISOString();
  const { data: chartDataResult } = useQuery({
    queryKey: ["engine-transactions-chart", merchantIds, chartStartISO, chartEndISO],
    queryFn: () => merchantService.getTransactionsChart(chartStartISO, chartEndISO, "PKR", merchantIds),
    enabled: merchantIds.length > 0,
    placeholderData: (prev) => prev,
  });
  const chartData = chartDataResult || [];

  // Engine aggregate stats (total/today volume, success rate, tx counts)
  const { data: engineStats, isLoading: statsLoading } = useQuery({
    queryKey: ["engine-stats", merchantIds],
    queryFn: () => adminService.getAdminEngineStats(merchantIds),
    enabled: merchantIds.length > 0,
    placeholderData: (prev) => prev,
  });


  const status = statusConfig[engine?.status?.toLowerCase()] || statusConfig.pending;
  const StatusIcon = status.icon;

  // Provider distribution - show all transactions (successful and failed)
  const providerCounts = transactions
    .reduce((acc, tx) => {
      const key = tx.provider_name || tx.bank_name || tx.bank_code || tx.client_bank_name;
      if (!key) return acc;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

  const providerData = Object.entries(providerCounts).map(([name, value]) => ({
    name,
    value,
  }));

  // With backend pagination, the current page transactions are already filtered and paginated.
  // We still need to apply local filtering for reseller/merchant since getPaginatedTransactions doesn't take those directly in filters object (it filters by the merchantIds array we passed)
  const filteredTxs = transactions.filter(tx => {
    if (txFilters.reseller !== "all" && tx.reseller_id !== txFilters.reseller) return false;
    if (txFilters.merchant !== "all" && tx.merchant_id !== txFilters.merchant) return false;
    return true;
  });

  const paginatedTxs = filteredTxs; // Data is already paginated by the backend!

  const tabItems = [
    { value: "analytics", icon: Activity, label: t("overview") },
    { value: "transactions", icon: Receipt, label: t("allTransactions") },
    { value: "resellers", icon: Users, label: t("resellers") },
    { value: "merchants", icon: Building2, label: t("merchants") },
    { value: "logs", icon: Shield, label: t("logs") },
  ];

  const statCards = [
    { title: t("todaysVolume"), value: `Rs${engineStats?.todayVolume}`, icon: TrendingUp, color: "bg-blue-100 text-blue-600" },
    { title: t("totalVolume"), value: `Rs${engineStats?.totalVolume}`, icon: Activity, color: "bg-emerald-100 text-emerald-600" },
    { title: t("successRate"), value: `${engineStats?.successRate}%`, icon: Percent, color: "bg-purple-100 text-purple-600" },
    { title: t("successful"), value: engineStats?.successTransactions, icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700" },
    { title: t("declined"), value: engineStats?.declinedTransactions, icon: XCircle, color: "bg-red-100 text-red-700" },
    { title: t("totalTransactions"), value: engineStats?.totalTransactions, icon: Receipt, color: "bg-pink-100 text-pink-600" },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      {!engine ? (
        <DetailHeaderSkeleton />
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <Link to={createPageUrl("AdminEngines")}>
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-16 h-16 rounded-2xl bg-sky-100 flex items-center justify-center">
                <Zap className="h-8 w-8 text-sky-600" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-2xl font-bold text-slate-900 break-words">{engine.name}</h1>
                  <Badge className={`${status.color} gap-1 border-0`}>
                    <StatusIcon className="h-3 w-3" />
                    {status.label}
                  </Badge>
                </div>
                <p className="text-slate-500 break-all">{engine.code} • {engine.email}</p>
              </div>
            </div>
          </div>
          <Link to={createPageUrl("AdminEngineForm") + `?id=${engineId}`}>
            <Button className="gap-2 w-full sm:w-auto">
              <Pencil className="h-4 w-4" />
              {t("editEngine")}
            </Button>
          </Link>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        {!engine || statsLoading
          ? Array.from({ length: 6 }).map((_, i) => <DetailStatCardSkeleton key={i} />)
          : statCards.map((stat, i) => (
            <DetailStatCard key={stat.title} {...stat} delay={i * 0.05} />
          ))}
      </div>

      <Tabs defaultValue="analytics" className="space-y-6">
        <TabsList className="bg-slate-100 w-full overflow-x-auto justify-start whitespace-nowrap">
          {tabItems.map(({ value, icon: Icon, label }) => (
            <TabsTrigger key={value} value={value} className="gap-2 shrink-0">
              <Icon className="h-4 w-4" />
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="analytics">
          <AnalyticsTab
            chartData={chartData}
            providerData={providerData}
            transactions={transactions}
            txLoading={txLoading}
            onView={setSelectedTx}
            merchants={merchants}
            resellers={resellers}
            t={t}
          />
        </TabsContent>

        <TabsContent value="transactions">
          <TransactionsTab
            filteredTxs={filteredTxs}
            paginatedTxs={paginatedTxs}
            page={page}
            pageSize={pageSize}
            totalPages={totalPages}
            txFilters={txFilters}
            setTxFilters={setTxFilters}
            setPage={setPage}
            onView={setSelectedTx}
            txLoading={txLoading}
            merchants={merchants}
            resellers={resellers}
            t={t}
          />
        </TabsContent>

        <TabsContent value="resellers">
          <ResellersTab
            resellers={resellers}
            statusConfig={statusConfig}
            t={t}
          />
        </TabsContent>

        <TabsContent value="merchants">
          <MerchantsTab
            merchants={merchants}
            statusConfig={statusConfig}
            t={t}
          />
        </TabsContent>
      </Tabs>

      {/* Transaction Detail Panel */}
      <TransactionDetailPanel
        transaction={selectedTx}
        open={!!selectedTx}
        onClose={() => setSelectedTx(null)}
        engine={engine}
        reseller={selectedTx?.reseller}
      />
    </div>
  );
}
